import { createMap } from "../omnia"
import { SelectionField } from "./types"

export type FieldSelectionContext = ReturnType<typeof getFieldSelectionContext>

interface Group { key: string, fields: SelectionField[] }
interface SelectedFields { [fieldId: number]: boolean }

/**
 * This function creates an object which supports the selection of fields, as implemented in "Fields.tsx"
 * Fields can be selected individually, or by groups of fields
 *
 * The object returned includes functions to check if a function or group is selected, or to change its selection state
 *
 * The object is designed to be used in a memo, and should be discarded/recreated each time the selection changes
 *
 * @param groups Groups of fields
 * @param fields Initial selection
 * @param setFields Called when the selection changes
 */
export function getFieldSelectionContext(groups: Group[], fields: SelectedFields, setFields: (fields: SelectedFields) => void) {

    // Selected fields
    const fieldsSet = new Set(getSelectedIds())

    // Map of groups with their current selection status
    const groupsMap = createMap(
        groups.map(g => ({
            ...g,
            selected: g.fields.every(f => isFieldSelected(f.fieldId))
        })),
        g => g.key
    )

    return {
        isGroupSelected,
        selectGroup,
        isFieldSelected,
        selectField: (fieldId: number, select: boolean) => selectFields([fieldId], select)
    }


    function getSelectedIds() {
        return Object.entries(fields).filter(e => e[1]).map(e => Number(e[0]))
    }

    function selectFields(fieldIds: number[], select: boolean) {

        fields = { ...fields }

        for (const fieldId of fieldIds) {
            fields[fieldId] = select
        }

        setFields(fields)

    }

    function isFieldSelected(fieldId: number) {
        return fieldsSet.has(fieldId)
    }

    function isGroupSelected(key: string) {
        return groupsMap.get(key)?.selected
    }

    function selectGroup(key: string, select: boolean) {
        const group = groupsMap.get(key)
        const fieldIds = group.fields.map(f => f.fieldId)
        selectFields(fieldIds, select)
    }

}
